import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { useMediaQuery } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router-dom";
import { AntTab, AntTabs } from "../../atleta/styles/PerfilPessoa";
import { useStyles } from "../styles/TabelaAtleta";
import TableAtletaResultado from "./TableAtletaResultado";
import { fetchClassesProvas } from "../hooks/fetchClassesProvas";
import { fetchProvasFiltradas } from "../hooks/fetchProvasFiltradas";
import { Alert } from "@material-ui/lab";

// Constants for mapping tab indices to values
const GENEROS = [
  { codigo: "F", nome: "Feminino" },
  { codigo: "M", nome: "Masculino" },
  { codigo: "A", nome: "Misto" },
];

const TIPOS_APARELHO = [
  { codigo: "I", nome: "Individual" },
  { codigo: "E", nome: "Sincronizado" },
];

const FASES = {
  f: "Final",
  e: "Eliminatória",
  s: "Semifinal",
};

export default function TabelaAtleta({ idEvento, evento }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  // Parse URL params
  const searchParams = new URLSearchParams(location.search);
  const urlGenero = searchParams.get("genero");
  const urlFormato = searchParams.get("formato");

  // Initialize state with URL params or defaults
  const getInitialGeneroIndex = () => {
    if (urlGenero) {
      const index = GENEROS.findIndex(
        (g) => g.codigo === urlGenero.toUpperCase()
      );
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  const getInitialFormatoIndex = () => {
    if (urlFormato) {
      const index = TIPOS_APARELHO.findIndex(
        (t) => t.codigo === urlFormato.toUpperCase()
      );
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  const [provaParams, setProvaParams] = useState({
    idProva: null,
    faseAtual: "f",
    tabIndexFormato: getInitialFormatoIndex(),
    tabIndexClasse: 0,
    tabIndexGenero: getInitialGeneroIndex(),
    tabIndexProva: 0,
  });

  // Fetch classes based on selected apparatus type and gender
  const {
    data: classesProvas = [],
    isLoading: classesLoading,
    isSuccess: classesSuccess,
  } = useQuery({
    queryKey: [
      "classesProvas",
      idEvento,
      TIPOS_APARELHO[provaParams.tabIndexFormato].codigo,
      GENEROS[provaParams.tabIndexGenero].codigo,
    ],
    queryFn: () =>
      fetchClassesProvas({
        idEvento,
        tipoAparelho: TIPOS_APARELHO[provaParams.tabIndexFormato].codigo,
        sexo: GENEROS[provaParams.tabIndexGenero].codigo,
      }),
    enabled: !!idEvento,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get current selected class
  const selectedClass = classesProvas[provaParams.tabIndexClasse];

  // Fetch provas based on all selected filters
  const { data: provas = [], isLoading: provasLoading } = useQuery({
    queryKey: [
      "provasFiltradas",
      idEvento,
      TIPOS_APARELHO[provaParams.tabIndexFormato].codigo,
      GENEROS[provaParams.tabIndexGenero].codigo,
      selectedClass?.id || null,
    ],
    queryFn: () =>
      fetchProvasFiltradas({
        idEvento,
        tipoAparelho: TIPOS_APARELHO[provaParams.tabIndexFormato].codigo,
        sexo: GENEROS[provaParams.tabIndexGenero].codigo,
        idClasse: selectedClass.id,
      }),
    enabled: !!idEvento && !!selectedClass?.id && classesSuccess,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update URL params
  const updateUrlParams = (formato, genero) => {
    const params = new URLSearchParams(location.search);
    params.set("formato", TIPOS_APARELHO[formato].codigo);
    params.set("genero", GENEROS[genero].codigo);
    history.replace({ search: params.toString() });
  };

  const handleChangeTabFormato = (event, newValue) => {
    // const newFormato = TIPOS_APARELHO[newValue].codigo;
    // Reset to first valid gender index (0) which is always valid
    setProvaParams({
      ...provaParams,
      faseAtual: "f",
      tabIndexFormato: newValue,
      tabIndexGenero: 0,
      tabIndexClasse: 0,
      tabIndexProva: 0,
    });
    updateUrlParams(newValue, 0);
  };

  const handleChangeTabGenero = (event, newValue) => {
    setProvaParams({
      ...provaParams,
      tabIndexGenero: newValue,
      faseAtual: "f",
      tabIndexClasse: 0,
      tabIndexProva: 0,
    });
    updateUrlParams(provaParams.tabIndexFormato, newValue);
  };

  const handleChangeTabClasse = (event, newValue) => {
    setProvaParams({
      ...provaParams,
      tabIndexClasse: newValue,
      faseAtual: "f",
      tabIndexProva: 0,
    });
  };

  const handleChangeTabProva = (event, newValue) => {
    setProvaParams({
      ...provaParams,
      faseAtual: "f",
      tabIndexProva: newValue,
    });
  };

  const handleChangeFase = (event, newValue) => {
    setProvaParams({ ...provaParams, faseAtual: newValue });
  };

  const nomeProva = (prova) => {
    if (!prova) return "";

    const fase = FASES[provaParams.faseAtual] || "";
    const genero = GENEROS.find((g) => g.codigo === prova.sexo)?.nome || "";
    const aparelho = prova.aparelho.descricao;
    const classe = prova.classe.descricao;

    return `${fase} ${genero} ${aparelho} ${classe}`;
  };

  const nomeProvaOption = (prova) => {
    const isPlataforma =
      prova.aparelho.descricao === "PLATAFORMA" ||
      prova.aparelho.descricao === "SINCRONIZADO PLATAFORMA";
    const isMisto = prova.sexo === "A";
    if (isMisto) {
      return `${prova.aparelho.descricao.replace("MISTO", "").replace('SINCRONIZADO','SINC')} ${
        prova.altura.descricao
      }`;
    }
    return isPlataforma
      ? prova.aparelho.descricao
      : `${prova.aparelho.descricao} ${prova.altura.descricao}`;
  };

  // When classes data changes (after gender/format change), ensure tabIndexClasse is valid
  useEffect(() => {
    if (!classesLoading && classesProvas.length > 0) {
      // If current index is out of bounds, reset to 0
      if (provaParams.tabIndexClasse >= classesProvas.length) {
        setProvaParams((prev) => ({
          ...prev,
          tabIndexClasse: 0,
          tabIndexProva: 0,
        }));
      }
    }
  }, [classesProvas, classesLoading, provaParams.tabIndexClasse]);

  // Update selected prova ID when provas change and ensure valid prova is selected
  useEffect(() => {
    if (!provasLoading && provas.length > 0) {
      const currentProva = provas[provaParams.tabIndexProva];

      if (currentProva && currentProva.id !== provaParams.idProva) {
        // Update idProva if it changed or is null
        setProvaParams((prev) => ({
          ...prev,
          idProva: currentProva.id,
        }));
      } else if (!currentProva && provaParams.tabIndexProva >= provas.length) {
        // Reset to first prova if index is out of bounds
        setProvaParams((prev) => ({
          ...prev,
          tabIndexProva: 0,
          idProva: provas[0].id,
        }));
      }
    }
  }, [provas, provasLoading, provaParams.tabIndexProva, provaParams.idProva]);

  const matchesMD = useMediaQuery((theme) => theme.breakpoints.up("md"));

  return (
    <div className={classes.root}>
      <Container maxWidth="md" className={classes.containerMD}>
        <Typography className={classes.title}>Placar Detalhado</Typography>
        <Typography variant="body2" className={classes.titleCaption}>
          Encontre a categoria que busca através dos filtros abaixo:
        </Typography>

        <Typography className={classes.tituloFiltro}>Formato</Typography>
        <AntTabs
          value={provaParams.tabIndexFormato}
          onChange={handleChangeTabFormato}
          className="centered"
        >
          {TIPOS_APARELHO.map((tipo, index) => (
            <AntTab key={index} label={tipo.nome} />
          ))}
        </AntTabs>

        <Typography className={classes.tituloFiltro}>Gênero</Typography>
        <AntTabs
          value={provaParams.tabIndexGenero}
          onChange={handleChangeTabGenero}
          className="centered"
        >
          {GENEROS.filter((genero) => {
            // Only show "Misto" when format is "Sincronizado" (code "E")
            if (genero.codigo === "A") {
              return TIPOS_APARELHO[provaParams.tabIndexFormato].codigo === "E";
            }
            return true;
          }).map((genero, index) => (
            <AntTab key={index} label={genero.nome} />
          ))}
        </AntTabs>

        {!classesLoading && classesProvas[0] && (
          <React.Fragment>
            <Typography className={classes.tituloFiltro}>Categoria</Typography>
            <AntTabs
              variant={
                classesProvas.length > 3 || !matchesMD
                  ? "scrollable"
                  : "standard"
              }
              value={provaParams.tabIndexClasse}
              onChange={handleChangeTabClasse}
              orientation={
                provas.length > 3 || !matchesMD ? "vertical" : "horizontal"
              }
            >
              {classesProvas.map((item, key) => (
                <AntTab
                  label={item.classe.sigla || item.classe.descricao}
                  key={key}
                />
              ))}
            </AntTabs>
          </React.Fragment>
        )}
        {!provasLoading && provas[0] && (
          <div className={classes.filtroProvas}>
            <Typography className={classes.tituloFiltro}>Prova</Typography>
            <AntTabs
              variant={
                provas.length > 3 || !matchesMD ? "scrollable" : "standard"
              }
              value={provaParams.tabIndexProva}
              onChange={handleChangeTabProva}
              orientation={
                provas.length > 3 || !matchesMD ? "vertical" : "horizontal"
              }
            >
              {provas.map((item, key) => (
                <AntTab label={nomeProvaOption(item)} key={key} />
              ))}
            </AntTabs>
          </div>
        )}
      </Container>
      <div className={classes.cardTabelaWrapper}>
        <Paper className={classes.cardTabela}>
          {classesLoading ? (
            <Alert
              severity="warning"
              style={{ padding: 20, margin: "auto", width: "fit-content" }}
            >
              <Typography> Carregando categorias... </Typography>
            </Alert>
          ) : classesProvas.length === 0 ? (
            <Alert
              severity="warning"
              style={{ padding: 20, margin: "auto", width: "fit-content" }}
            >
              <Typography>
                Nenhuma categoria encontrada para esta categoria.
              </Typography>
            </Alert>
          ) : provasLoading ? (
            <Alert
              severity="info"
              style={{ padding: 20, margin: "auto", width: "fit-content" }}
            >
              <Typography>Carregando provas...</Typography>
            </Alert>
          ) : provas.length > 0 && provas[provaParams.tabIndexProva] ? (
            <React.Fragment>
              <Typography className={classes.tableTitle}>
                {nomeProva(provas[provaParams.tabIndexProva])}
              </Typography>
              <AntTabs
                value={provaParams.faseAtual}
                onChange={handleChangeFase}
                centered
              >
                {provas[provaParams.tabIndexProva].id_etapa_e && (
                  <AntTab value="e" label="Eliminatória" />
                )}
                {provas[provaParams.tabIndexProva].id_etapa_s && (
                  <AntTab value="s" label="Semi Final" />
                )}
                {provas[provaParams.tabIndexProva].id_etapa_f && (
                  <AntTab value="f" label="Final" />
                )}
              </AntTabs>

              {evento && provaParams.idProva && (
                <TableAtletaResultado
                  provaParams={{ ...provaParams, evento }}
                />
              )}
            </React.Fragment>
          ) : (
            <Alert
              severity="warning"
              style={{ padding: 20, margin: "auto", width: "fit-content" }}
            >
              <Typography>
                Nenhuma prova encontrada para os filtros selecionados
              </Typography>
            </Alert>
          )}
        </Paper>
      </div>
    </div>
  );
}
